/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module usePdpQueryParams
 *
 * @author Neeraj Raj
 */

import { useCallback, useEffect, useRef, useState } from "react";
import useDebounce from "./useDebounceCallback";
import { usePathname } from "next/navigation";
import { pdpInternalUrlQueryParams } from "@/apiUtils/constants";

/**
 * @description This Hook handles the PDP query params
 */
const usePdpQueryParams = () => {
  const pathname = usePathname();
  const optionRef = useRef<{ replace?: boolean }>({ replace: true });
  const debounce = useDebounce(100);

  const getWindowQueryParams = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const searchStateLocal: {
      [key: string]: string | null;
    } = {};
    searchParams.forEach((value, key) => {
      searchStateLocal[key] = value;
    });
    return searchStateLocal;
  }, []);

  const [searchState, setSearchState] = useState<{ [key: string]: string | null }>(() => {
    return typeof window !== "undefined" ? getWindowQueryParams() : {};
  });

  useEffect(() => {
    setSearchState(getWindowQueryParams());
  }, [getWindowQueryParams]);

  const pathnameRef = useRef(pathname);

  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    debounce(() => {
      const searchParams = new URLSearchParams();

      Object.entries(searchState).forEach(([key, value]) => {
        if (value) {
          searchParams.set(key, value);
        }
      });

      if (searchParams.get(pdpInternalUrlQueryParams.QUERY_KEY)) {
        searchParams.delete(pdpInternalUrlQueryParams.QUERY_KEY);
      }

      const search = searchParams.toString();
      const newUrl = `${window.location.protocol}//${window.location.host}${pathnameRef.current}${search ? `?${search}` : ""}`;

      if (optionRef.current.replace) {
        window.history.replaceState({ path: newUrl }, "", newUrl);
      } else {
        window.history.pushState({ path: newUrl }, "", newUrl);
      }
    });
  }, [searchState, debounce]);

  /**
   * @description This function updates query params with key and value
   */
  const updatePdpQueryParams = useCallback(
    (
      newSate: {
        [key: string]: string | null;
      },
      options: { history?: string },
      url?: string,
    ) => {
      optionRef.current = { replace: options?.history === "replace" };

      if (url) pathnameRef.current = url;
      if (newSate) {
        setSearchState((prevState) => {
          return {
            ...prevState,
            ...newSate,
          };
        });
      }
    },
    [],
  );

  return {
    queryParams: searchState,
    updatePdpQueryParams,
    getWindowQueryParams,
  };
};

export default usePdpQueryParams;
